/**
 * NotificationsLocationSync
 *
 * Escucha cambios de `location.pathname` y reconcilia el notificationsStore
 * marcando como leídas las notifs cuyo `actionPath` matchea la ruta actual.
 *
 * Casos cubiertos:
 *  - /enrollments → markAllReadByKind('enrollment')
 *  - /chat/:agentId → markRead sobre cada notif chat con ese agentId
 *  - /updates → markAllReadByKind('update') + markAllReadByKind('update-failed')
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotificationsStore } from '@/store/notificationsStore';

function matchPath(pathname: string, actionPath?: string): boolean {
  if (!actionPath) return false;
  // Soportar paths parametrizados básicos: /chat/:agentId
  const a = actionPath.split('/').filter(Boolean);
  const b = pathname.split('/').filter(Boolean);
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const seg = a[i]!;
    if (seg.startsWith(':')) continue;
    if (seg !== b[i]) return false;
  }
  return true;
}

export function NotificationsLocationSync() {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);

  useEffect(() => {
    const prev = pathnameRef.current;
    pathnameRef.current = location.pathname;
    if (prev === location.pathname) return;

    const store = useNotificationsStore.getState();
    const items = store.items;

    if (location.pathname.startsWith('/enrollments')) {
      store.markAllReadByKind('enrollment');
      return;
    }
    if (location.pathname.startsWith('/updates')) {
      store.markAllReadByKind('update');
      store.markAllReadByKind('update-failed');
      return;
    }
    if (location.pathname.startsWith('/chat/')) {
      // Marcar leídas las notifs de chat cuyo actionPath matchea esta ruta exacta.
      for (const n of items) {
        if (n.kind !== 'chat') continue;
        if (matchPath(location.pathname, n.actionPath) && !n.read) {
          store.markRead(n.id);
        }
      }
    }
  }, [location.pathname]);

  return null;
}

export default NotificationsLocationSync;
